import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import axios from 'axios';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Briefcase, Shield, Calendar, Save, Edit2, Camera, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import UserAvatar, { PALETTE } from '../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Resize image to a max dimension and convert to JPEG data URI (smaller than PNG)
const resizeImageToDataURI = (file, maxDim = 384, quality = 0.85) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      try {
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const ProfilePage = () => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    team: '',
    avatar_color: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        mobile: user.mobile || '',
        team: user.team || 'verwaltung',
        avatar_color: user.avatar_color || PALETTE[0]
      });
    }
  }, [user]);

  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/profile`, formData, { headers: getAuthHeader() });
      if (refreshUser) await refreshUser();
      toast.success(t('profile.saved'));
      setIsEditing(false);
    } catch (error) {
      toast.error(t('profile.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so user can pick same file again
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.avatarInvalid'));
      return;
    }
    setUploading(true);
    try {
      const dataUri = await resizeImageToDataURI(file, 384, 0.85);
      await axios.post(`${API_URL}/profile/avatar`, { image_base64: dataUri }, { headers: getAuthHeader() });
      if (refreshUser) await refreshUser();
      toast.success(t('profile.avatarUploaded'));
    } catch (error) {
      const detail = error?.response?.data?.detail || t('profile.avatarError');
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    setUploading(true);
    try {
      await axios.delete(`${API_URL}/profile/avatar`, { headers: getAuthHeader() });
      if (refreshUser) await refreshUser();
      toast.success(t('profile.avatarRemoved'));
    } catch (error) {
      toast.error(t('profile.avatarError'));
    } finally {
      setUploading(false);
    }
  };

  const handleColorPick = async (color) => {
    setFormData(prev => ({ ...prev, avatar_color: color }));
    try {
      await axios.put(`${API_URL}/profile`, { avatar_color: color }, { headers: getAuthHeader() });
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(t('profile.error'));
    }
  };

  const getTeamLabel = (team) => {
    const teams = { marketing: t('teams.marketing'), website: t('teams.website'), verwaltung: t('teams.verwaltung') };
    return teams[team] || team;
  };

  const getTeamColor = (team) => {
    const colors = {
      marketing: 'bg-[#F3E8FF] text-[#7E22CE]',
      website: 'bg-[#E0F2FE] text-[#0369A1]',
      verwaltung: 'bg-[#FFEDD5] text-[#C2410C]'
    };
    return colors[team] || colors.verwaltung;
  };

  if (!user) return null;
  const headerBg = user.avatar_color || PALETTE[0];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="profile-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('profile.title')}
          </h1>
          <p className="text-zinc-600">{t('profile.subtitle')}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border-2 border-zinc-200 overflow-hidden"
        >
          {/* Profile Header */}
          <div
            className="p-8 text-white"
            style={{ background: `linear-gradient(135deg, ${headerBg}, ${headerBg}cc)` }}
          >
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Avatar + upload overlay */}
              <div className="relative group">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      data-testid="profile-avatar-image"
                    />
                  ) : (
                    <span className="text-4xl font-bold">{user.name?.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="profile-avatar-upload-btn"
                  className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white text-zinc-800 flex items-center justify-center shadow-md hover:scale-105 transition-transform"
                  title={t('profile.uploadPicture')}
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarFile}
                  data-testid="profile-avatar-file-input"
                />
              </div>

              <div className="text-center sm:text-left">
                <h2 className="text-2xl font-bold mb-1">{user.name}</h2>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-white/80">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start gap-3 mt-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTeamColor(user.team)}`}>
                    {getTeamLabel(user.team)}
                  </span>
                  {user.role === 'admin' && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white">
                      Admin
                    </span>
                  )}
                  {user.avatar_url && (
                    <button
                      type="button"
                      onClick={handleAvatarRemove}
                      disabled={uploading}
                      data-testid="profile-avatar-remove-btn"
                      className="px-3 py-1 rounded-full text-xs font-medium bg-white/15 hover:bg-white/25 inline-flex items-center gap-1 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      {t('profile.removePicture')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Color Picker */}
          <div className="p-6 sm:p-8 border-b border-zinc-100">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <Label className="text-sm font-semibold text-zinc-900">{t('profile.avatarColor')}</Label>
                <p className="text-xs text-zinc-500 mt-0.5">{t('profile.avatarColorHint')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="profile-color-picker">
              {PALETTE.map(color => {
                const selected = (user.avatar_color || formData.avatar_color) === color;
                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorPick(color)}
                    data-testid={`color-${color}`}
                    title={color}
                    aria-pressed={selected}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                      selected ? 'ring-2 ring-offset-2 ring-zinc-900 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  >
                    {selected && <Check className="w-4 h-4 text-white" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-6 sm:p-8">
            {!isEditing ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="p-4 bg-zinc-50 rounded-2xl">
                    <div className="flex items-center gap-3 text-zinc-500 mb-2">
                      <User className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.name')}</span>
                    </div>
                    <p className="text-lg font-semibold text-zinc-900">{user.name}</p>
                  </div>

                  <div className="p-4 bg-zinc-50 rounded-2xl">
                    <div className="flex items-center gap-3 text-zinc-500 mb-2">
                      <Mail className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.email')}</span>
                    </div>
                    <p className="text-lg font-semibold text-zinc-900">{user.email}</p>
                  </div>

                  <div className="p-4 bg-zinc-50 rounded-2xl">
                    <div className="flex items-center gap-3 text-zinc-500 mb-2">
                      <Phone className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.mobile')}</span>
                    </div>
                    <p className="text-lg font-semibold text-zinc-900">
                      {user.mobile || <span className="text-zinc-400">{t('profile.notSet')}</span>}
                    </p>
                  </div>

                  <div className="p-4 bg-zinc-50 rounded-2xl">
                    <div className="flex items-center gap-3 text-zinc-500 mb-2">
                      <Briefcase className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.team')}</span>
                    </div>
                    <p className="text-lg font-semibold text-zinc-900">{getTeamLabel(user.team)}</p>
                  </div>

                  <div className="p-4 bg-zinc-50 rounded-2xl">
                    <div className="flex items-center gap-3 text-zinc-500 mb-2">
                      <Shield className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.role')}</span>
                    </div>
                    <p className="text-lg font-semibold text-zinc-900">
                      {user.role === 'admin' ? 'Administrator' : t('profile.employee')}
                    </p>
                  </div>

                  <div className="p-4 bg-zinc-50 rounded-2xl">
                    <div className="flex items-center gap-3 text-zinc-500 mb-2">
                      <Calendar className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.memberSince')}</span>
                    </div>
                    <p className="text-lg font-semibold text-zinc-900">
                      {user.created_at && format(new Date(user.created_at), 'd. MMMM yyyy', { locale: getLocale() })}
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => setIsEditing(true)}
                  className="w-full bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full py-6"
                  data-testid="edit-profile-button"
                >
                  <Edit2 className="w-5 h-5 mr-2" />
                  {t('profile.edit')}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <Label htmlFor="name">{t('profile.name')}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2"
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t('profile.email')}</Label>
                  <Input id="email" value={user.email} disabled className="mt-2 bg-zinc-100" />
                  <p className="text-xs text-zinc-500 mt-1">{t('profile.emailCannotChange')}</p>
                </div>
                <div>
                  <Label htmlFor="mobile">{t('profile.mobile')}</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="+49 123 456789"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className="mt-2"
                    data-testid="mobile-input"
                  />
                </div>
                <div>
                  <Label htmlFor="team">{t('profile.team')}</Label>
                  <Select value={formData.team} onValueChange={(value) => setFormData({ ...formData, team: value })}>
                    <SelectTrigger className="mt-2" data-testid="team-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marketing">{t('teams.marketing')}</SelectItem>
                      <SelectItem value="website">{t('teams.website')}</SelectItem>
                      <SelectItem value="verwaltung">{t('teams.verwaltung')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outline"
                    className="flex-1 rounded-full py-6"
                    data-testid="cancel-edit-button"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full py-6"
                    data-testid="save-profile-button"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {loading ? t('common.loading') : t('common.save')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
